import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import { SkeletonTable } from './components/Skeleton';

interface JournalLigne {
  compte_id: string;
  compte_libelle: string;
  debit_centimes: number;
  credit_centimes: number;
}

interface JournalEntry {
  id: string;
  numero: number;
  date: string;
  libelle: string;
  statut_validation: 'validated' | 'locked';
  lignes: JournalLigne[];
}

interface JournalProps {
  entrepriseId: string;
  exerciceId: string;
}

const formatCurrency = (centimes: number) => {
  const euros = centimes / 100;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(euros);
};

const Journal: React.FC<JournalProps> = ({ exerciceId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['journal', exerciceId],
    queryFn: () => api.journal.get(exerciceId),
  });
  const entries = data?.entries ?? [];

  const exportCSV = () => {
    if (!entries.length) return;
    const headers = ['EcritureNum', 'Date', 'Libellé', 'CompteNum', 'CompteLibelle', 'Débit (€)', 'Crédit (€)', 'Statut'];
    const rows = entries.flatMap((entry: JournalEntry) =>
      entry.lignes.map((l: JournalLigne) => [entry.numero.toString(), new Date(entry.date).toISOString().slice(0, 10), entry.libelle, l.compte_id, l.compte_libelle, (l.debit_centimes / 100).toFixed(2), (l.credit_centimes / 100).toFixed(2), entry.statut_validation])
    );
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `journal-${exerciceId}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  if (isLoading) return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Journal comptable</h2>
      <SkeletonTable rows={8} />
    </div>
  );
  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur: {(error as Error).message}</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Journal comptable</h2>
        <button onClick={exportCSV} disabled={!entries.length} className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Exporter CSV</button>
      </div>
      {entries.length === 0 ? <p className="text-sm text-gray-500">Aucune écriture pour cet exercice.</p> : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">#</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compte</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé compte</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Débit</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Crédit</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Statut</th>
              </tr>
            </thead>
            <tbody>
              {entries.flatMap((entry: JournalEntry) =>
                entry.lignes.map((ligne: JournalLigne, idx: number) => {
                  const isFirst = idx === 0;
                  return (
                    <tr key={`${entry.id}-${ligne.compte_id}-${idx}`} className="hover:bg-gray-50 transition-colors">
                      {isFirst && (
                        <>
                          <td rowSpan={entry.lignes.length} className="px-4 py-3 text-gray-900 border-b border-gray-100 font-bold">{entry.numero}</td>
                          <td rowSpan={entry.lignes.length} className="px-4 py-3 text-gray-700 border-b border-gray-100">{new Date(entry.date).toLocaleDateString('fr-FR')}</td>
                        </>
                      )}
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{ligne.compte_id}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{ligne.compte_libelle}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(ligne.debit_centimes)}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(ligne.credit_centimes)}</td>
                      {isFirst && (
                        <td rowSpan={entry.lignes.length} className="px-4 py-3 border-b border-gray-100">
                          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            entry.statut_validation === 'validated' ? 'bg-green-100 text-green-700'
                            : entry.statut_validation === 'locked' ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-500'
                          }`}>
                            {entry.statut_validation === 'validated' ? 'Validé'
                              : entry.statut_validation === 'locked' ? 'Verrouillé'
                              : 'Brouillon'}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Journal;
